import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import { Plus, Users, Edit, Trash2, Calendar, ArrowLeft, BookOpen, Tag, Search, Info, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GroupeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getPatientLabel, getPatientLabelPlural } = useSettings();

  // States
  const [groupDetails, setGroupDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isCreateGroupNoteOpen, setIsCreateGroupNoteOpen] = useState(false);
  const [editingGroupNote, setEditingGroupNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form states
  const [newGroupNote, setNewGroupNote] = useState({
    titre: '',
    contenu: ''
  });

  const [editGroupNote, setEditGroupNote] = useState({
    titre: '',
    contenu: ''
  });

  // Load group details
  const loadGroupDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/groupes/${id}/details`);
      setGroupDetails(response.data);
    } catch (error) {
      console.error('Error loading group details:', error);
      toast.error('Erreur lors du chargement des détails du groupe');
      navigate('/groupes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroupDetails();
  }, [id]);

  // Create group note
  const handleCreateGroupNote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/groupes/${id}/notes`, {
        ...newGroupNote,
        groupe_id: id
      });
      toast.success('Note de groupe créée avec succès');
      setIsCreateGroupNoteOpen(false);
      setNewGroupNote({ titre: '', contenu: '' });
      loadGroupDetails();
    } catch (error) {
      toast.error('Erreur lors de la création de la note');
    }
  };

  // Edit group note
  const handleEditGroupNote = (note) => {
    setEditingGroupNote(note.id);
    setEditGroupNote({
      titre: note.titre,
      contenu: note.contenu
    });
  };

  // Update group note
  const handleUpdateGroupNote = async (e) => {
    e.preventDefault();
    if (!editingGroupNote) return;

    try {
      await axios.put(`${API}/group-notes/${editingGroupNote}`, editGroupNote);
      toast.success('Note de groupe modifiée avec succès');
      setEditingGroupNote(null);
      setEditGroupNote({ titre: '', contenu: '' });
      loadGroupDetails();
    } catch (error) {
      toast.error('Erreur lors de la modification de la note');
    }
  };

  const cancelEditGroupNote = () => {
    setEditingGroupNote(null);
    setEditGroupNote({ titre: '', contenu: '' });
  };

  // Delete group note
  const handleDeleteGroupNote = async (noteId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette note de groupe ?')) {
      try {
        await axios.delete(`${API}/group-notes/${noteId}`);
        toast.success('Note de groupe supprimée avec succès');
        loadGroupDetails();
      } catch (error) {
        toast.error('Erreur lors de la suppression de la note');
      }
    }
  };

  // Filter notes based on search
  const filteredGroupNotes = groupDetails?.group_notes?.filter(note =>
    searchTerm === '' || 
    note.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.contenu.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const filteredPatientNotes = groupDetails?.patient_notes?.filter(note =>
    searchTerm === '' || 
    note.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.contenu.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.patient.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.patient.prenom.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-blue-50">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-600">Chargement...</div>
          </div>
        </main>
      </div>
    );
  }

  if (!groupDetails) return null;

  return (
    <div className="py-6">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with back button */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/groupes')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux groupes
          </Button>
          
          <div className="flex items-center gap-3">
            <div 
              className="w-6 h-6 rounded-full" 
              style={{backgroundColor: groupDetails.groupe.couleur}}
            ></div>
            <h1 className="text-3xl font-bold text-gray-900">{groupDetails.groupe.nom}</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8 border-b">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Vue d'ensemble
            </button>
            <button
              onClick={() => setActiveTab('students')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'students'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {getPatientLabelPlural()} ({groupDetails.stats.total_patients})
            </button>
            <button
              onClick={() => setActiveTab('group-notes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'group-notes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Notes du groupe ({groupDetails.stats.total_group_notes})
            </button>
            <button
              onClick={() => setActiveTab('student-notes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'student-notes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Notes des {getPatientLabelPlural()} ({groupDetails.stats.total_patient_notes})
            </button>
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Informations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-600">Description</p>
                  <p className="text-gray-800">{groupDetails.groupe.description || 'Aucune description'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Date de création</p>
                  <p className="text-gray-800">{new Date(groupDetails.groupe.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Couleur</p>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{backgroundColor: groupDetails.groupe.couleur}}
                    ></div>
                    <span className="text-gray-800">{groupDetails.groupe.couleur}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {getPatientLabelPlural()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{groupDetails.stats.total_patients}</div>
                <p className="text-sm text-gray-600">{getPatientLabelPlural()} dans ce groupe</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <div className="text-2xl font-bold text-green-600">{groupDetails.stats.total_group_notes}</div>
                  <p className="text-sm text-gray-600">Notes du groupe</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">{groupDetails.stats.total_patient_notes}</div>
                  <p className="text-sm text-gray-600">Notes des {getPatientLabelPlural()}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div>
            {groupDetails.patients.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Aucun {getPatientLabel()} dans ce groupe.</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {groupDetails.patients.map(patient => (
                  <Card key={patient.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{patient.prenom} {patient.nom}</h3>
                          {patient.etablissement && (
                            <p className="text-sm text-gray-600">{patient.etablissement}</p>
                          )}
                          {patient.categorie_libre && (
                            <Badge variant="secondary" className="mt-1 text-xs">
                              {patient.categorie_libre}
                            </Badge>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{patient.notes_count} note(s)</div>
                          {patient.date_naissance && (
                            <div className="text-xs text-gray-500">
                              {new Date(patient.date_naissance).toLocaleDateString('fr-FR')}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Group Notes Tab */}
        {activeTab === 'group-notes' && (
          <div>
            {/* Header with create button */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Notes du groupe</h3>
              <Dialog open={isCreateGroupNoteOpen} onOpenChange={setIsCreateGroupNoteOpen}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Nouvelle note
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-x-hidden flex flex-col">
                  <DialogHeader className="flex-shrink-0">
                    <DialogTitle>Ajouter une note au groupe</DialogTitle>
                    <DialogDescription>
                      Cette note sera associée au groupe "{groupDetails.groupe.nom}"
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex-1 overflow-y-auto overflow-x-hidden">
                    <form onSubmit={handleCreateGroupNote} className="space-y-4">
                      <div>
                        <Label htmlFor="group-note-titre">Titre *</Label>
                        <Input
                          id="group-note-titre"
                          value={newGroupNote.titre}
                          onChange={(e) => setNewGroupNote({...newGroupNote, titre: e.target.value})}
                          placeholder="Titre de la note..."
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="group-note-contenu">Contenu *</Label>
                        <RichTextEditor
                          key={`group-note-${isCreateGroupNoteOpen}`}
                          value={newGroupNote.contenu}
                          onChange={(value) => setNewGroupNote(prev => ({...prev, contenu: value}))}
                          placeholder="Rédigez votre note ici..."
                        />
                      </div>
                      <div className="flex-shrink-0 pt-4 sticky bottom-0 bg-white">
                        <Button type="submit" className="w-full">Créer la note</Button>
                      </div>
                    </form>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Rechercher dans les notes du groupe..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Group Notes List */}
            {filteredGroupNotes.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {searchTerm ? 'Aucune note trouvée.' : 'Aucune note pour ce groupe.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredGroupNotes.map(note => (
                  <Card key={note.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      {editingGroupNote === note.id ? (
                        // Edit mode
                        <form onSubmit={handleUpdateGroupNote} className="space-y-4">
                          <div>
                            <Label htmlFor={`edit-group-note-titre-${note.id}`}>Titre</Label>
                            <Input
                              id={`edit-group-note-titre-${note.id}`}
                              value={editGroupNote.titre}
                              onChange={(e) => setEditGroupNote({...editGroupNote, titre: e.target.value})}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor={`edit-group-note-contenu-${note.id}`}>Contenu</Label>
                            <RichTextEditor
                              key={`edit-group-note-${note.id}`}
                              value={editGroupNote.contenu}
                              onChange={(value) => setEditGroupNote(prev => ({...prev, contenu: value}))}
                              placeholder="Rédigez votre note ici..."
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button type="submit" size="sm">Sauvegarder</Button>
                            <Button type="button" variant="outline" size="sm" onClick={cancelEditGroupNote}>
                              Annuler
                            </Button>
                          </div>
                        </form>
                      ) : (
                        // View mode
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-lg">{note.titre}</h4>
                            </div>
                            <div className="text-sm text-gray-600 mb-3">
                              Créée le {new Date(note.created_at).toLocaleDateString('fr-FR')} à {new Date(note.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                            <div 
                              className="text-gray-800 note-content overflow-hidden" 
                              dangerouslySetInnerHTML={{ __html: note.contenu }}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleEditGroupNote(note)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleDeleteGroupNote(note.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Student Notes Tab */}
        {activeTab === 'student-notes' && (
          <div>
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Rechercher dans les notes des élèves..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Student Notes List */}
            {filteredPatientNotes.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {searchTerm ? 'Aucune note trouvée.' : `Aucune note des ${getPatientLabelPlural()} dans ce groupe.`}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPatientNotes.map(note => (
                  <Card key={note.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold text-lg">{note.titre}</h4>
                            <Badge variant="outline">
                              {note.patient.prenom} {note.patient.nom}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-600 mb-3">
                            Créée le {new Date(note.created_at).toLocaleDateString('fr-FR')} à {new Date(note.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          <div 
                            className="text-gray-800 whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{ __html: note.contenu }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};


export default GroupeDetail;
